import React, { useState, useEffect, useMemo, useRef } from "react";
import { useWatch } from "react-hook-form";
import FormTitle from "../../../components/FormTitle";
import Input from "../../../components/FormInputs";
import CustomButton from "../../../components/AddButton";
import CustomModal from "../../../components/Modal";
import { Plus } from "../../../common/svg";
import PhoneInput from "../../../components/phoneinput";
import { ShippingDetail } from "../../../service/interfaces/ApiResponse.interface";
import { vendorShippingDetailsProps } from "../../../service/interfaces/props.interface";

const BillingDetails: React.FC<vendorShippingDetailsProps> = ({
  errors,
  register,
  control,
  setValue,
  unregister,
  setBillingDetailData,
  setShippingDetailsData,
  vendorPhoneNumber,
  vendorEmailId,
  vendorAddress1,
  vendorAddress2,
  vendorCity,
  vendorState,
  vendorCountry,
  vendorPincode,
  vendorCountryCode,
  initialShippingDetails = [],
  useSameContact,
  setUseSameContact,
}) => {
  // Use localStorage to persist checkbox states across tab switches
  const LOCAL_STORAGE_KEY_VENDOR_CONTACT = "useVendorContactState";
  const LOCAL_STORAGE_KEY_VENDOR_BILLING_ADDRESS = "sameBillingAddressForms";

  // Get initial checkbox state from localStorage or default to false
  const getInitialUseVendorContact = () => {
    try {
      const storedValue = localStorage.getItem(LOCAL_STORAGE_KEY_VENDOR_CONTACT);
      return storedValue === "1";
    } catch (error) {
      return false;
    }
  };

  // Initialize state from props or localStorage
  const [useVendorContact, setUseVendorContact] = useState<boolean>(() => {
    if (useSameContact !== undefined) {
      return useSameContact;
    }
    return getInitialUseVendorContact();
  });

  // Track forms with a more robust data structure that includes a unique ID for each form
  const [formData, setFormData] = useState<Array<{ id: number, data: ShippingDetail }>>(() => {
    if (initialShippingDetails.length > 0) {
      return initialShippingDetails.map((detail, index) => ({
        id: index,
        data: detail || {}
      }));
    } else {
      return [{ id: 0, data: {} }];
    }
  });

  const [isModalOpen, setModalOpen] = useState(false);
  const [formIdToRemove, setFormIdToRemove] = useState<number | null>(null);
  const [removedShippingIds, setRemovedShippingIds] = useState<string[]>([]); // Added to track removed IDs
  const [nextFormId, setNextFormId] = useState(
    initialShippingDetails.length > 0 ? initialShippingDetails.length : 1
  );

  // Get initial same billing address states from localStorage or default to empty object
  const getInitialSameBillingAddressForms = () => {
    try {
      const storedValue = localStorage.getItem(LOCAL_STORAGE_KEY_VENDOR_BILLING_ADDRESS);
      return storedValue ? JSON.parse(storedValue) : {};
    } catch (error) {
      return {};
    }
  };

  const [sameBillingAddressForms, setSameBillingAddressForms] = useState<Record<number, boolean>>(
    getInitialSameBillingAddressForms()
  );

  const watchFields = useWatch({ control });

  // Flag to track if initial vendor details have been applied
  const initialVendorDetailsApplied = useRef(false);
  const isInitialized = useRef(false);

  // Helper function to check if field has error
  const getFieldError = (
    type: "billing" | "shipping",
    index: number,
    fieldName: string
  ) => {
    return type === "billing"
      ? (errors.billingAddress as any)?.[fieldName]?.message || ""
      : (errors.shippingDetails?.[index] as any)?.[fieldName]?.message || "";
  };

  // Helper function to check if field has error
  const hasFieldError = (
    type: "billing" | "shipping",
    index: number,
    fieldName: string
  ) => {
    return type === "billing"
      ? !!(errors.billingAddress as any)?.[fieldName]
      : !!(errors.shippingDetails?.[index] as any)?.[fieldName];
  };

  // Sync with parent component's state
  useEffect(() => {
    if (setUseSameContact && useVendorContact !== useSameContact) {
      setUseSameContact(useVendorContact);
    }
  }, [useVendorContact, setUseSameContact, useSameContact]);

  // Initialize form with stored values on component mount
  useEffect(() => {
    if (!isInitialized.current) {
      // Restore form values if checkbox was checked before refresh
      if (useVendorContact) {
        const timer = setTimeout(() => {
          if (vendorPhoneNumber) setValue("billingAddress.phoneNumber", vendorPhoneNumber);
          if (vendorEmailId) setValue("billingAddress.emailId", vendorEmailId);
          if (vendorAddress1) setValue("billingAddress.address1", vendorAddress1);
          if (vendorAddress2) setValue("billingAddress.address2", vendorAddress2);
          if (vendorCity) setValue("billingAddress.city", vendorCity);
          if (vendorState) setValue("billingAddress.state", vendorState);
          if (vendorCountry) setValue("billingAddress.country", vendorCountry);
          if (vendorPincode) setValue("billingAddress.pincode", vendorPincode);
          if (vendorCountryCode) setValue("billingAddress.countryCode", vendorCountryCode);
        }, 100);

        return () => clearTimeout(timer);
      }

      // Restore shipping form values that were using same billing address
      Object.entries(sameBillingAddressForms).forEach(([formIdStr, isChecked]) => {
        if (isChecked) {
          const formId = parseInt(formIdStr, 10);
          const formIndex = formData.findIndex(form => form.id === formId);
          if (formIndex !== -1) {
            const timer = setTimeout(() => {
              copyBillingAddressToShippingForm(formIndex);
            }, 150);
            return () => clearTimeout(timer);
          }
        }
      });

      isInitialized.current = true;
    }
  }, [useVendorContact, formData, sameBillingAddressForms]);

  // Apply vendor details to billing fields if checkbox is checked
  useEffect(() => {
    // Only apply vendor details on mount if checkbox is checked and not yet applied
    if (useVendorContact && !initialVendorDetailsApplied.current && isInitialized.current) {
      // Short delay to ensure form is ready
      const timer = setTimeout(() => {
        if (vendorPhoneNumber) setValue("billingAddress.phoneNumber", vendorPhoneNumber);
        if (vendorEmailId) setValue("billingAddress.emailId", vendorEmailId);
        if (vendorAddress1) setValue("billingAddress.address1", vendorAddress1);
        if (vendorAddress2) setValue("billingAddress.address2", vendorAddress2);
        if (vendorCity) setValue("billingAddress.city", vendorCity);
        if (vendorState) setValue("billingAddress.state", vendorState);
        if (vendorCountry) setValue("billingAddress.country", vendorCountry);
        if (vendorPincode) setValue("billingAddress.pincode", vendorPincode);
        if (vendorCountryCode) setValue("billingAddress.countryCode", vendorCountryCode);

        initialVendorDetailsApplied.current = true;
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [
    useVendorContact,
    vendorPhoneNumber,
    vendorEmailId,
    vendorAddress1,
    vendorAddress2,
    vendorCity,
    vendorState,
    vendorCountry,
    vendorPincode,
    vendorCountryCode,
    setValue,
    isInitialized
  ]);

  // Initialize countryCode and setup sameBillingAddressForms on component mount
  useEffect(() => {
    // Set default country code when component loads if watchFields has data but countryCode is missing
    if (
      watchFields?.billingAddress?.phoneNumber &&
      !watchFields?.billingAddress?.countryCode
    ) {
      setValue("billingAddress.countryCode", ""); // Set default country code
    }

    // Initialize shipping details country codes
    formData.forEach((form, index) => {
      if (form.data.phoneNumber && !form.data.countryCode) {
        setValue(`shippingDetails[${index}].countryCode`, "");
      }
    });

    // Initialize the sameBillingAddressForms state with default values
    // but only for forms that don't already have a state in localStorage
    const initialSameBillingAddressState = { ...sameBillingAddressForms };
    formData.forEach(form => {
      if (initialSameBillingAddressState[form.id] === undefined) {
        initialSameBillingAddressState[form.id] = false;
      }
    });
    setSameBillingAddressForms(initialSameBillingAddressState);

    // Save to localStorage
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY_VENDOR_BILLING_ADDRESS, JSON.stringify(initialSameBillingAddressState));
    } catch (error) {
      console.error("Failed to save to localStorage:", error);
    }
  }, []);

  // Handler for "Use Same Contact Details" checkbox
  const handleUseVendorContactChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setUseVendorContact(isChecked);

    // Update parent state if available
    if (setUseSameContact) {
      setUseSameContact(isChecked);
    }

    // Save to localStorage (1 for true, 0 for false)
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY_VENDOR_CONTACT, isChecked ? "1" : "0");
    } catch (error) {
      console.error("Failed to save to localStorage:", error);
    }

    if (isChecked) {
      // Set billing address fields with vendor details
      if (vendorPhoneNumber) setValue("billingAddress.phoneNumber", vendorPhoneNumber);
      if (vendorEmailId) setValue("billingAddress.emailId", vendorEmailId);
      if (vendorAddress1) setValue("billingAddress.address1", vendorAddress1);
      if (vendorAddress2) setValue("billingAddress.address2", vendorAddress2);
      if (vendorCity) setValue("billingAddress.city", vendorCity);
      if (vendorState) setValue("billingAddress.state", vendorState);
      if (vendorCountry) setValue("billingAddress.country", vendorCountry);
      if (vendorPincode) setValue("billingAddress.pincode", vendorPincode);
      if (vendorCountryCode) setValue("billingAddress.countryCode", vendorCountryCode);
    } else {
      // Clear billing address fields when unchecked
      setValue("billingAddress.phoneNumber", "");
      setValue("billingAddress.emailId", "");
      setValue("billingAddress.address1", "");
      setValue("billingAddress.address2", "");
      setValue("billingAddress.city", "");
      setValue("billingAddress.state", "");
      setValue("billingAddress.country", "");
      setValue("billingAddress.pincode", "");
      setValue("billingAddress.countryCode", "");
    }
  };

  // Function to copy billing address to a specific shipping form
  const copyBillingAddressToShippingForm = (index: number) => {
    if (watchFields?.billingAddress) {
      setValue(`shippingDetails[${index}].address1`, watchFields.billingAddress.address1 || "");
      setValue(`shippingDetails[${index}].address2`, watchFields.billingAddress.address2 || "");
      setValue(`shippingDetails[${index}].city`, watchFields.billingAddress.city || "");
      setValue(`shippingDetails[${index}].state`, watchFields.billingAddress.state || "");
      setValue(`shippingDetails[${index}].country`, watchFields.billingAddress.country || "");
      setValue(`shippingDetails[${index}].pincode`, watchFields.billingAddress.pincode || "");
      setValue(`shippingDetails[${index}].phoneNumber`, watchFields.billingAddress.phoneNumber || "");
      setValue(`shippingDetails[${index}].countryCode`, watchFields.billingAddress.countryCode || "");
      setValue(`shippingDetails[${index}].emailId`, watchFields.billingAddress.emailId || "");
    }
  };


  // Effect to keep shipping address in sync with billing address when toggled
  useEffect(() => {
    if (watchFields?.billingAddress && isInitialized.current) {
      // For each form that has "use same billing address" checked, update its fields
      Object.entries(sameBillingAddressForms).forEach(([formIdStr, isChecked]) => {
        if (isChecked) {
          // Find the index in formData that corresponds to this formId
          const formIndex = formData.findIndex(form => form.id === parseInt(formIdStr, 10));
          if (formIndex !== -1) {
            copyBillingAddressToShippingForm(formIndex);
          }
        }
      });
    }
  }, [watchFields?.billingAddress, isInitialized]);

  // Update billing details in state when form data changes
  useEffect(() => {
    if (watchFields?.billingAddress) {
      // If using vendor contact, use vendor details but preserve any form values that might have been entered
      const billingDetails = {
        address1: useVendorContact && vendorAddress1 ? vendorAddress1 : watchFields.billingAddress?.address1 || "",
        address2: useVendorContact && vendorAddress2 ? vendorAddress2 : watchFields.billingAddress?.address2 || "",
        city: useVendorContact && vendorCity ? vendorCity : watchFields.billingAddress?.city || "",
        state: useVendorContact && vendorState ? vendorState : watchFields.billingAddress?.state || "",
        country: useVendorContact && vendorCountry ? vendorCountry : watchFields.billingAddress?.country || "",
        pincode: useVendorContact && vendorPincode ? vendorPincode : watchFields.billingAddress?.pincode || "",
        phoneNumber: useVendorContact && vendorPhoneNumber ? vendorPhoneNumber : watchFields.billingAddress?.phoneNumber || "",
        countryCode: useVendorContact && vendorCountryCode ? vendorCountryCode : watchFields.billingAddress?.countryCode || "",
        emailId: useVendorContact && vendorEmailId ? vendorEmailId : watchFields.billingAddress?.emailId || "",
      };

      setBillingDetailData(billingDetails);
    }
  }, [
    watchFields,
    setBillingDetailData,
    useVendorContact,
    vendorPhoneNumber,
    vendorEmailId,
    vendorAddress1,
    vendorAddress2,
    vendorCity,
    vendorState,
    vendorCountry,
    vendorPincode,
    vendorCountryCode,
  ]);

  // Update shipping details in state when form data changes
  useEffect(() => {
    // Extract shipping details from formData and watchFields
    const shippingDetails = formData.map((form, index) => {
      // Check if this detail has an ID and it's in the removed list
      if (form.data?.shippingAddressId && removedShippingIds.includes(form.data.shippingAddressId)) {
        return null;
      }

      return {
        address1: watchFields.shippingDetails?.[index]?.address1 || "",
        address2: watchFields.shippingDetails?.[index]?.address2 || "",
        city: watchFields.shippingDetails?.[index]?.city || "",
        pincode: watchFields.shippingDetails?.[index]?.pincode || "",
        state: watchFields.shippingDetails?.[index]?.state || "",
        country: watchFields.shippingDetails?.[index]?.country || "",
        phoneNumber: watchFields.shippingDetails?.[index]?.phoneNumber || "",
        countryCode: watchFields.shippingDetails?.[index]?.countryCode || "",
        emailId: watchFields.shippingDetails?.[index]?.emailId || "",
        // Include the ID if it exists
        shippingAddressId: form.data?.shippingAddressId || undefined,
      };
    }).filter(Boolean);

    setShippingDetailsData(shippingDetails);
  }, [watchFields, formData, removedShippingIds, setShippingDetailsData]);

  const addNewShippingForm = () => {
    // Add new form with unique ID
    const newId = nextFormId;
    setNextFormId(prev => prev + 1);
    setFormData(prev => [...prev, { id: newId, data: {} }]);

    // Initialize the checkbox state for the new form
    setSameBillingAddressForms(prev => {
      const updated = {
        ...prev,
        [newId]: false
      };

      // Save updated state to localStorage
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY_VENDOR_BILLING_ADDRESS, JSON.stringify(updated));
      } catch (error) {
        console.error("Failed to save to localStorage:", error);
      }

      return updated;
    });
  };

  const openModal = (formId: number) => {
    // Store the index of the form to remove
    const formIndex = formData.findIndex(form => form.id === formId);
    if (formIndex !== -1) {
      setFormIdToRemove(formId);
      setModalOpen(true);
    }
  };

  const closeModal = () => setModalOpen(false);

  // Improved handleConfirmRemove function
  const handleConfirmRemove = () => {
    if (formIdToRemove !== null) {
      // Find the index of the form in the formData array
      const formIndex = formData.findIndex(form => form.id === formIdToRemove);

      if (formIndex !== -1) {
        // Get the form to be removed
        const formToRemove = formData[formIndex];

        // If there's a shippingAddressId, track it for removal
        if (formToRemove.data?.shippingAddressId) {
          setRemovedShippingIds(prev => [...prev, formToRemove.data.shippingAddressId!]);
        }
        if (unregister) {
          unregister(`shippingDetails.${formIndex}`);
          for (let i = formIndex + 1; i < formData.length; i++) {
            unregister(`shippingDetails.${i}`);
          }
        }

        // Remove the form from formData
        setFormData(prev => {
          const newFormData = [...prev];
          newFormData.splice(formIndex, 1);
          return newFormData;
        });

        // Remove the checkbox state for this form
        setSameBillingAddressForms(prev => {
          const newState = { ...prev };
          delete newState[formIdToRemove];

          // Save updated state to localStorage
          try {
            localStorage.setItem(LOCAL_STORAGE_KEY_VENDOR_BILLING_ADDRESS, JSON.stringify(newState));
          } catch (error) {
            console.error("Failed to save to localStorage:", error);
          }

          return newState;
        });
      }

      // Reset the formIdToRemove and close modal
      setFormIdToRemove(null);
      closeModal();
    }
  };

  // Function to handle individual "Use Same billingAddress" checkbox change
  const handleUseSameBillingAddressChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    formId: number
  ) => {
    const isChecked = e.target.checked;

    // Update the state to track which form is using same billing address
    setSameBillingAddressForms(prev => {
      const updated = {
        ...prev,
        [formId]: isChecked
      };

      // Save updated state to localStorage
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY_VENDOR_BILLING_ADDRESS, JSON.stringify(updated));
      } catch (error) {
        console.error("Failed to save to localStorage:", error);
      }

      return updated;
    });
    const formIndex = formData.findIndex(form => form.id === formId);
    if (isChecked && formIndex !== -1) {
      copyBillingAddressToShippingForm(formIndex);
    } else if (formIndex !== -1) {
      setValue(`shippingDetails[${formIndex}].address1`, "");
      setValue(`shippingDetails[${formIndex}].address2`, "");
      setValue(`shippingDetails[${formIndex}].city`, "");
      setValue(`shippingDetails[${formIndex}].state`, "");
      setValue(`shippingDetails[${formIndex}].country`, "");
      setValue(`shippingDetails[${formIndex}].pincode`, "");
      setValue(`shippingDetails[${formIndex}].phoneNumber`, "");
      setValue(`shippingDetails[${formIndex}].countryCode`, "");
      setValue(`shippingDetails[${formIndex}].emailId`, "");
    }
  };

  return (
    <div className="bg-white p-6 rounded-[20px] font-poppins mb-6">
      {/* Billing Address Section */}
      <div className="flex items-center justify-between mb-4">
        <FormTitle title="Billing Address Details" />
        <div className="flex items-center gap-2">
          <>
            <input
              type="checkbox"
              className="w-[20px] h-[20px]"
              checked={useVendorContact}
              onChange={(e) => handleUseVendorContactChange(e)}
            />
            <label className="text-[16px] font-[400] font-poppins">
              Use Same Company Details
            </label>
          </>
        </div>
      </div>

      {/* Billing Address Fields */}
      <div className="mt-4 grid grid-cols-4 gap-x-6 gap-y-4">
        <Input
          label="Address Line 1"
          name="billingAddress.address1"
          required
          errors={
            hasFieldError("billing", 0, "address1")
              ? {
                "billingAddress.address1": {
                  message: getFieldError("billing", 0, "address1"),
                },
              }
              : {}
          }
          register={register}
          placeholder="Enter Address Line 1"
          defaultValue={useVendorContact && vendorAddress1 ? vendorAddress1 : undefined}
        />
        <Input
          label="Address Line 2"
          name="billingAddress.address2"
          errors={
            hasFieldError("billing", 0, "address2")
              ? {
                "billingAddress.address2": {
                  message: getFieldError("billing", 0, "address2"),
                },
              }
              : {}
          }
          register={register}
          placeholder="Enter Address Line 2"
          defaultValue={useVendorContact && vendorAddress2 ? vendorAddress2 : undefined}
        />
        <Input
          label="City"
          name="billingAddress.city"
          required
          errors={
            hasFieldError("billing", 0, "city")
              ? {
                "billingAddress.city": {
                  message: getFieldError("billing", 0, "city"),
                },
              }
              : {}
          }
          register={register}
          placeholder="Enter City"
          defaultValue={useVendorContact && vendorCity ? vendorCity : undefined}
        />
        <Input
          label="Pincode"
          name="billingAddress.pincode"
          required
          errors={
            hasFieldError("billing", 0, "pincode")
              ? {
                "billingAddress.pincode": {
                  message: getFieldError("billing", 0, "pincode"),
                },
              }
              : {}
          }
          register={register}
          placeholder="Enter Pincode"
          type="number"
          maxLength={6}
          defaultValue={useVendorContact && vendorPincode ? vendorPincode : undefined}
        />
        <Input
          label="State"
          name="billingAddress.state"
          required
          errors={
            hasFieldError("billing", 0, "state")
              ? {
                "billingAddress.state": {
                  message: getFieldError("billing", 0, "state"),
                },
              }
              : {}
          }
          register={register}
          placeholder="Enter State"
          defaultValue={useVendorContact && vendorState ? vendorState : undefined}
        />
        <Input
          label="Country"
          name="billingAddress.country"
          required
          errors={
            hasFieldError("billing", 0, "country")
              ? {
                "billingAddress.country": {
                  message: getFieldError("billing", 0, "country"),
                },
              }
              : {}
          }
          register={register}
          placeholder="Enter Country"
          defaultValue={useVendorContact && vendorCountry ? vendorCountry : undefined}
        />
        <PhoneInput
          label="Phone Number"
          name="billingAddress.phoneNumber"
          defaultCountry="IN"
          type="tel"
          errors={
            hasFieldError("billing", 0, "phoneNumber")
              ? {
                "billingAddress.phoneNumber": {
                  message: getFieldError("billing", 0, "phoneNumber"),
                },
              }
              : {}
          }
          control={control}
          maxLength={10}
          placeholder="Enter your phone number"
          value={
            useVendorContact && vendorPhoneNumber
              ? vendorPhoneNumber
              : watchFields?.billingAddress?.phoneNumber || ""
          }
          defaultValue={{
            countryCode: useVendorContact && vendorCountryCode
              ? vendorCountryCode
              : watchFields?.billingAddress?.countryCode || "",
            phoneNumber:
              useVendorContact && vendorPhoneNumber
                ? vendorPhoneNumber
                : watchFields?.billingAddress?.phoneNumber || "",
          }}
          onChange={({ countryCode, phoneNumber, dialCode }) => {
            setValue("billingAddress.phoneNumber", phoneNumber);
            setValue("billingAddress.countryCode", countryCode);

            // Update any shipping forms that are using the same billing address
            Object.entries(sameBillingAddressForms).forEach(([formIdStr, isChecked]) => {
              if (isChecked) {
                const formIndex = formData.findIndex(form => form.id === parseInt(formIdStr, 10));
                if (formIndex !== -1) {
                  setValue(`shippingDetails[${formIndex}].phoneNumber`, phoneNumber);
                  setValue(`shippingDetails[${formIndex}].countryCode`, countryCode);
                  setValue(`shippingDetails[${formIndex}].dialCode`, dialCode);
                }
              }
            });
          }}
        />
        <Input
          label="Mail ID"
          name="billingAddress.emailId"
          required
          defaultValue={useVendorContact && vendorEmailId ? vendorEmailId : undefined}
          errors={
            hasFieldError("billing", 0, "emailId")
              ? {
                "billingAddress.emailId": {
                  message: getFieldError("billing", 0, "emailId"),
                },
              }
              : {}
          }
          register={register}
          placeholder={
            useVendorContact && vendorEmailId
              ? vendorEmailId
              : "Enter email address"
          }
          onChange={(emailValue: string) => {
            Object.entries(sameBillingAddressForms).forEach(([formIdStr, isChecked]) => {
              if (isChecked) {
                const formIndex = formData.findIndex(form => form.id === parseInt(formIdStr, 10));
                if (formIndex !== -1) {
                  setValue(`shippingDetails[${formIndex}].emailId`, emailValue);
                }
              }
            });
          }}
        />
      </div>
      <br />

      {/* Shipping Address Section */}
      <div className="flex items-center justify-between">
        <FormTitle title="Shipping Address Details" />
        <div className="flex items-center gap-4">
          <CustomButton label="" icon={Plus} onClick={addNewShippingForm} />
        </div>
      </div>

      {/* Shipping Address Fields */}
      <div className="flex flex-col gap-6">
        {formData.map((form, index) => (
          <div
            key={form.id}
            className="border-b border-gray-200 pb-4"
          >
            <div className="flex items-center justify-end mb-2 gap-4">
              <p className="text-[14px] font-[600] mt-1">
                #{(index + 1).toString().padStart(2, "0")}
              </p>
              {formData.length > 1 && (
                <button
                  onClick={() => openModal(form.id)}
                  type="button"
                  className="bg-red-500 text-white p-1 rounded-full shadow-md hover:bg-red-600 transition-all"
                  aria-label="Remove"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>

            <div className="mt-4 grid grid-cols-4 gap-x-6 gap-y-4">
              <Input
                label="Address Line 1"
                name={`shippingDetails[${index}].address1`}
                errors={
                  hasFieldError("shipping", index, "address1")
                    ? {
                      [`shippingDetails[${index}].address1`]: {
                        message: getFieldError("shipping", index, "address1"),
                      },
                    }
                    : {}
                }
                register={register}
                placeholder="Enter Address Name"
                defaultValue={form.data?.address1 || ""}
              />
              <Input
                label="Address Line 2"
                name={`shippingDetails[${index}].address2`}
                errors={
                  hasFieldError("shipping", index, "address2")
                    ? {
                      [`shippingDetails[${index}].address2`]: {
                        message: getFieldError("shipping", index, "address2"),
                      },
                    }
                    : {}
                }
                register={register}
                placeholder="Enter Shipping Address Line"
                defaultValue={form.data?.address2 || ""}
              />
              <Input
                label="City"
                name={`shippingDetails[${index}].city`}
                errors={
                  hasFieldError("shipping", index, "city")
                    ? {
                      [`shippingDetails[${index}].city`]: {
                        message: getFieldError("shipping", index, "city"),
                      },
                    }
                    : {}
                }
                register={register}
                placeholder="Enter Shipping City"
                defaultValue={initialShippingDetails[index]?.city || ""}
              />
              <Input
                label="Pincode"
                name={`shippingDetails[${index}].pincode`}
                type="number"
                maxLength={6}
                errors={
                  hasFieldError("shipping", index, "pincode")
                    ? {
                      [`shippingDetails[${index}].pincode`]: {
                        message: getFieldError("shipping", index, "pincode"),
                      },
                    }
                    : {}
                }
                register={register}
                placeholder="Enter Shipping Pincode"
                defaultValue={initialShippingDetails[index]?.pincode || ""}
              />
              <Input
                label="State"
                name={`shippingDetails[${index}].state`}
                errors={
                  hasFieldError("shipping", index, "state")
                    ? {
                      [`shippingDetails[${index}].state`]: {
                        message: getFieldError("shipping", index, "state"),
                      },
                    }
                    : {}
                }
                register={register}
                placeholder="Enter Shipping State"
                defaultValue={initialShippingDetails[index]?.state || ""}
              />
              <Input
                label="Country"
                name={`shippingDetails[${index}].country`}
                errors={
                  hasFieldError("shipping", index, "country")
                    ? {
                      [`shippingDetails[${index}].country`]: {
                        message: getFieldError("shipping", index, "country"),
                      },
                    }
                    : {}
                }
                register={register}
                placeholder="Enter Shipping Country"
                defaultValue={initialShippingDetails[index]?.country || ""}
              />
              <PhoneInput
                label="Phone Number"
                name={`shippingDetails[${index}].phoneNumber`}
                defaultCountry="IN"
                type="tel"
                errors={
                  hasFieldError("shipping", index, "phoneNumber")
                    ? {
                      [`shippingDetails[${index}].phoneNumber`]: {
                        message: getFieldError("shipping", index, "phoneNumber"),
                      },
                    }
                    : {}
                }
                maxLength={10}
                placeholder="Enter Shipping Phone Number"
                control={control}
                defaultValue={{
                  countryCode:
                    watchFields?.shippingDetails?.[index]?.countryCode || "",
                  phoneNumber:
                    watchFields?.shippingDetails?.[index]?.phoneNumber || "",
                }}
                value={watchFields?.shippingDetails?.[index]?.phoneNumber || ""}
                onChange={({ countryCode, phoneNumber, dialCode }) => {
                  setValue(
                    `shippingDetails[${index}].phoneNumber`,
                    phoneNumber
                  );
                  setValue(
                    `shippingDetails[${index}].countryCode`,
                    countryCode
                  );
                  // setValue(`shippingDetails[${index}].dialCode`, dialCode);
                }}
              />
              <Input
                label="Mail ID"
                name={`shippingDetails[${index}].emailId`}
                errors={
                  hasFieldError("shipping", index, "emailId")
                    ? {
                      [`shippingDetails[${index}].emailId`]: {
                        message: getFieldError("shipping", index, "emailId"),
                      },
                    }
                    : {}
                }
                register={register}
                placeholder="Enter Email Address"
                defaultValue={initialShippingDetails[index]?.emailId || ""}
              />
            </div>

            {/* Individual checkbox for each shipping form */}
            <div className="flex items-center justify-end gap-2 mt-4">
              <input
                type="checkbox"
                id={`useSameBillingAddress-${index}`}
                className="w-[20px] h-[20px]"
                checked={sameBillingAddressForms[index] || false}
                onChange={(e) => handleUseSameBillingAddressChange(e, index)}
              />
              <label
                htmlFor={`useSameBillingAddress-${index}`}
                className="text-[16px] font-[400] whitespace-nowrap font-poppins"
              >
                Use Same billingAddress
              </label>
            </div>
          </div>
        ))}
      </div>

      <CustomModal
        isOpen={isModalOpen}
        content={
          <div className="flex flex-col items-center">
            <p className="text-[18px] font-[600] text-[#4B4B4B]">
              Are you sure you want to remove this form?
            </p>
            <div className="mt-4 flex gap-4">
              <button
                onClick={handleConfirmRemove}
                type="button"
                className="bg-red-500 rounded-[5px] h-[37px] w-[99px] text-white"
              >
                Yes
              </button>
              <button
                onClick={closeModal}
                type="button"
                className="bg-gray-500 rounded-[5px] h-[37px] w-[99px] text-white"
              >
                No
              </button>
            </div>
          </div>
        }
        onClose={closeModal}
      />
    </div>
  );
};

export default BillingDetails;